import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CircleX, Eye, Search, X } from "lucide-react";
import MenuIconTooltip from "./MenuIconTooltip";
import LibraryScrollPane from "./LibraryScrollPane";
import { MENU_LIBRARY_TOOLTIPS } from "../constants/menuTooltips";
import { useAuth } from "./AuthGate";
import { useAccountTextScale } from "../constants/textScalePreference";
import { getAppPortalRoot } from "../utils/appPortal";
import { clamp, readFrameBox, readWindowPos, windowMetrics } from "../utils/libraryWindow";
import { useFloatingWindowLayer } from "../utils/floatingWindows";
import {
  SEARCH_MIN_LENGTH,
  SEARCH_RESULT_LIMIT,
  buildBookSearchIndex,
  foldSearchText,
  searchBookIndex,
} from "../utils/librarySearchText";
import "./LibraryCharacters.css";
import "./LibrarySearch.css";

const POS_KEY = "vocabularium_library_search_pos";
const SEARCH_DELAY = 220;

export default function LibrarySearch({
  bookId = "",
  chapters = [],
  openToken = 0,
  onShowHit = () => {},
}) {
  const { user } = useAuth();
  const [textScale] = useAccountTextScale(user?.sub || "");
  const windowGrow = textScale > 1 ? 20 : 0;
  const windowGrowRef = useRef(windowGrow);
  windowGrowRef.current = windowGrow;
  const dragRef = useRef(null);
  const posRef = useRef(readWindowPos(POS_KEY));
  const seenTokenRef = useRef(openToken);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const indexRef = useRef({ chapters: null, index: null });
  const searchedRef = useRef("");
  const queryBookRef = useRef(bookId);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [searched, setSearched] = useState("");
  const [dragging, setDragging] = useState(false);
  const [leftRatio, setLeftRatio] = useState(posRef.current.leftRatio);
  const [topRatio, setTopRatio] = useState(posRef.current.topRatio);
  const [frameBox, setFrameBox] = useState(readFrameBox);
  const layer = useFloatingWindowLayer(open);

  if (queryBookRef.current !== bookId) {
    queryBookRef.current = bookId;
    if (query) setQuery("");
    if (searched) setSearched("");
  }

  useEffect(() => {
    if (seenTokenRef.current === openToken) return;
    seenTokenRef.current = openToken;
    setOpen((current) => !current);
  }, [openToken]);

  useEffect(() => {
    if (!open) return undefined;
    const frame = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  useEffect(() => {
    if (query === searched) return undefined;
    const timer = setTimeout(() => setSearched(query), SEARCH_DELAY);
    return () => clearTimeout(timer);
  }, [query, searched]);

  useEffect(() => {
    if (!open) return undefined;
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer = frame && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [open]);

  useEffect(() => {
    const onMove = (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      const box = readFrameBox();
      const size = windowMetrics(box, windowGrowRef.current);
      const maxLeft = Math.max(box.width - size.visualWidth, 0);
      const maxTop = Math.max(box.height - size.visualHeight, 0);
      const left = clamp(event.clientX - drag.offsetX - box.left, 0, maxLeft);
      const top = clamp(event.clientY - drag.offsetY - box.top, 0, maxTop);
      const nextLeft = maxLeft ? left / maxLeft : 0;
      const nextTop = maxTop ? top / maxTop : 0;
      posRef.current = { leftRatio: nextLeft, topRatio: nextTop };
      setLeftRatio(nextLeft);
      setTopRatio(nextTop);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      setDragging(false);
      try {
        localStorage.setItem(POS_KEY, JSON.stringify(posRef.current));
      } catch {
        /* la position reste en mémoire pour cette visite */
      }
    };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, []);

  const folded = foldSearchText(searched);
  const ready = chapters.length > 0;
  const result = useMemo(() => {
    if (!open || !ready || folded.length < SEARCH_MIN_LENGTH) return { hits: [], truncated: false };
    if (indexRef.current.chapters !== chapters) {
      indexRef.current = { chapters, index: buildBookSearchIndex(chapters) };
    }
    return searchBookIndex(indexRef.current.index, searched);
  }, [open, ready, chapters, folded, searched]);

  useLayoutEffect(() => {
    if (searchedRef.current === folded) return;
    searchedRef.current = folded;
    const node = scrollRef.current;
    if (node) node.scrollTop = 0;
  }, [folded]);

  const windowStyle = useMemo(() => {
    const size = windowMetrics(frameBox, windowGrow);
    const maxLeft = Math.max(frameBox.width - size.visualWidth, 0);
    const maxTop = Math.max(frameBox.height - size.visualHeight, 0);
    return {
      width: `${size.designWidth}px`,
      height: `${size.designHeight}px`,
      minWidth: `${size.designWidth}px`,
      minHeight: `${size.designHeight}px`,
      left: `${frameBox.left + leftRatio * maxLeft}px`,
      top: `${frameBox.top + topRatio * maxTop}px`,
      transform: `scale(${size.scale})`,
      transformOrigin: "top left",
    };
  }, [frameBox, leftRatio, topRatio, windowGrow]);

  const startDrag = (event) => {
    if (event.target.closest("button, input, .library-characters__search")) return;
    const windowEl = event.currentTarget.parentElement;
    if (!windowEl) return;
    const rect = windowEl.getBoundingClientRect();
    dragRef.current = {
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
    };
    setDragging(true);
    event.preventDefault();
  };

  const closeWindow = useCallback(() => setOpen(false), []);

  const portalRoot = getAppPortalRoot();
  if (!portalRoot || !bookId || !open) return null;

  const pending = query !== searched;
  const message = !ready
    ? "Le livre se charge…"
    : folded.length < SEARCH_MIN_LENGTH
      ? pending && foldSearchText(query).length >= SEARCH_MIN_LENGTH
        ? ""
        : "Tapez au moins deux lettres."
      : !result.hits.length && !pending
        ? "Aucun passage ne contient ce terme."
        : "";

  return createPortal(
    <div className="library-characters library-search">
      <section
        className="library-characters__window library-search__window"
        style={{ ...windowStyle, zIndex: layer.zIndex }}
        aria-label="Rechercher dans le livre"
        onPointerDownCapture={layer.bringToFront}
      >
        <header
          className={`library-characters__banner library-search__banner${
            dragging ? " library-characters__banner--dragging" : ""
          }`}
          onPointerDown={startDrag}
        >
          <span />
          <h2 className="library-characters__title">Rechercher dans le livre…</h2>
          <button
            type="button"
            className="library-characters__close"
            aria-label="Fermer la recherche"
            onClick={closeWindow}
          >
            <X aria-hidden="true" />
          </button>
          <div className="library-characters__search library-search__bar">
            <Search size={14} strokeWidth={1.75} aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              placeholder="un mot, une expression…"
              aria-label="Mot ou expression à chercher dans le livre"
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") setSearched(query);
              }}
            />
            <button
              type="button"
              className="library-characters__search-clear"
              aria-label="Effacer la recherche"
              disabled={!query}
              onClick={() => {
                setQuery("");
                setSearched("");
                inputRef.current?.focus({ preventScroll: true });
              }}
            >
              <CircleX size={14} strokeWidth={1.75} aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="library-characters__body">
          <LibraryScrollPane
            className="library-characters__pane--passages"
            scrollerClassName="library-characters__passages"
            scrollRef={scrollRef}
            contentKey={`${folded}:${result.hits.length}:${message}`}
          >
            {result.hits.map((hit) => (
              <div key={hit.key} className="library-characters__passage">
                <p className="library-characters__passage-text">
                  {hit.segments.map((segment, index) =>
                    segment.hit ? (
                      <mark key={index} className="library-search__hit">
                        {segment.text}
                      </mark>
                    ) : (
                      <span key={index}>{segment.text}</span>
                    )
                  )}
                </p>
                <div className="library-characters__passage-actions">
                  <MenuIconTooltip
                    tooltip={MENU_LIBRARY_TOOLTIPS.openPassage}
                    placement="above"
                    portal
                    zIndex={20}
                  >
                    <button
                      type="button"
                      className="library-characters__icon-btn"
                      aria-label={MENU_LIBRARY_TOOLTIPS.openPassage}
                      onClick={() => onShowHit(hit)}
                    >
                      <Eye size={16} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </MenuIconTooltip>
                </div>
              </div>
            ))}
            {result.truncated ? (
              <p className="library-characters__empty">
                {`Seuls les ${SEARCH_RESULT_LIMIT} premiers passages sont affichés. Précisez la recherche.`}
              </p>
            ) : null}
            {message ? <p className="library-characters__empty">{message}</p> : null}
          </LibraryScrollPane>
        </div>
      </section>
    </div>,
    portalRoot
  );
}
